import { useEffect, useState } from 'react';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';

type ExportableItems = {
    images: Image3D[];
    structureSets: ReadonlyArray<StructureSetReadModel>;
    doses: DoseMatrix[];
    registrations: RigidRegistration[];
};

const useExportSelections = (
    exportableStructureSets: ReadonlyArray<StructureSetReadModel> = [],
    exportableDoses: DoseMatrix[] = [],
    modalOpen?: boolean,
    exportableRegistrations: RigidRegistration[] = []
) => {
    const [selectedImages, setSelectedImages] = useState<Image3D[]>([]);
    // Structure-set selection is keyed by SOP Instance UID — the natural
    // key for the export service and the read model. No live entity is
    // retained in selection state.
    const [selectedStructureSetUids, setSelectedStructureSetUids] = useState<
        string[]
    >([]);
    const [selectedDoses, setSelectedDoses] = useState<DoseMatrix[]>([]);
    const [selectedRegistrations, setSelectedRegistrations] = useState<
        RigidRegistration[]
    >([]);
    // Per-structure-set ROI subset selection, keyed by SS SOP UID then
    // holding the selected ROI numbers.
    const [structureSelections, setStructureSelections] = useState<
        Record<string, number[]>
    >({});

    useEffect(() => {
        const newStructureSets = exportableStructureSets.filter(
            (ss) => ss.isNew
        );
        const newDoses = exportableDoses.filter((dose) => dose.isNew);
        const newRegistrations = exportableRegistrations.filter(
            (reg) => reg.isNew
        );
        if (newStructureSets?.length > 0) {
            setSelectedStructureSetUids(
                newStructureSets.map((ss) => ss.dicomUid)
            );
            newStructureSets.forEach((structureSet) => {
                initializeStructureSelections(structureSet);
            });
        }
        if (newDoses?.length > 0) {
            setSelectedDoses(newDoses);
        }
        if (newRegistrations.length > 0) {
            setSelectedRegistrations(newRegistrations);
        }
        // Exhaustive deps are ignored here as other deps will cause an infinite loop
        // because exportableStructureSets and exportableDoses are recreated on each render, but they
        // also cannot be memoized as underlying objects are not immutable so changes to them wouldn't
        // be detected correctly, causing parts of UI not to update as expected.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    const initializeStructureSelections = (
        structureSet: StructureSetReadModel
    ) => {
        const approvedStructures = structureSet.roiArray.filter(
            (structure) => structure.isApproved
        );
        const initialRois = approvedStructures.length
            ? approvedStructures
            : structureSet.roiArray.filter(
                  (structure) =>
                      structure.approvalStatus !== DICOMApprovalStatus.REJECTED
              );
        setStructureSelections((prev) => ({
            ...prev,
            [structureSet.dicomUid]: initialRois.map((roi) => roi.roiNumber),
        }));
    };

    const handleSelectImage = (image: Image3D) => {
        setSelectedImages((prev) =>
            prev.includes(image)
                ? prev.filter((id) => id !== image)
                : [...prev, image]
        );
    };

    const handleSelectStructureSet = (structureSet: StructureSetReadModel) => {
        const uid = structureSet.dicomUid;
        setSelectedStructureSetUids((prev) =>
            prev.includes(uid)
                ? prev.filter((id) => id !== uid)
                : [...prev, uid]
        );

        if (!structureSelections[uid]) {
            initializeStructureSelections(structureSet);
        }
    };

    const handleSelectStructure = (
        structureSetUid: string,
        roiNumber: number
    ) => {
        setStructureSelections((prev) => {
            const currentSelections = prev[structureSetUid] || [];
            const updatedSelections = currentSelections.includes(roiNumber)
                ? currentSelections.filter((n) => n !== roiNumber)
                : [...currentSelections, roiNumber];

            return {
                ...prev,
                [structureSetUid]: updatedSelections,
            };
        });
    };

    const handleSelectDose = (dose: DoseMatrix) => {
        setSelectedDoses((prev) =>
            prev.includes(dose)
                ? prev.filter((id) => id !== dose)
                : [...prev, dose]
        );
    };

    const handleSelectRegistration = (registration: RigidRegistration) => {
        setSelectedRegistrations((prev) =>
            prev.includes(registration)
                ? prev.filter((r) => r !== registration)
                : [...prev, registration]
        );
    };

    const handleSelectAll = (
        exportableItems: ExportableItems,
        selectAll: boolean
    ): void => {
        if (selectAll) {
            setSelectedImages(exportableItems.images);
            setSelectedStructureSetUids(
                exportableItems.structureSets.map((ss) => ss.dicomUid)
            );
            setSelectedDoses(exportableItems.doses);
            setSelectedRegistrations(exportableItems.registrations);
            exportableItems.structureSets.forEach((ss) => {
                if (!structureSelections[ss.dicomUid]) {
                    initializeStructureSelections(ss);
                }
            });
        } else {
            setSelectedImages([]);
            setSelectedStructureSetUids([]);
            setSelectedDoses([]);
            setSelectedRegistrations([]);
        }
    };

    const resetSelections = () => {
        setSelectedImages([]);
        setSelectedStructureSetUids([]);
        setSelectedDoses([]);
        setSelectedRegistrations([]);
        setStructureSelections({});
    };

    return {
        selectedImages,
        selectedStructureSetUids,
        selectedDoses,
        selectedRegistrations,
        structureSelections,
        handleSelectImage,
        handleSelectStructureSet,
        handleSelectStructure,
        handleSelectDose,
        handleSelectRegistration,
        handleSelectAll,
        resetSelections,
    };
};

export default useExportSelections;
